import type { FC, ReactNode } from "react";
import { useContext, useEffect, useRef, useState } from "react";

import type { ISecondarySideBarContext } from "app/components/sidebar3/secondary-side-bar/SecondarySideBarContext";
import { SecondarySideBarContext } from "app/components/sidebar3/secondary-side-bar/SecondarySideBarContext";
import type { INavItem } from "app/components/sidebar3/types";

import { createSideNavHierarchy } from "./DigitalSuiteNavBarUtils";

interface IDigitalSuiteMainNavBarBody {
    navObj: INavItem[];
}

export const DigitalSuiteGenericNavBarBody: FC<IDigitalSuiteMainNavBarBody> = ({ navObj }) => {
    const context = useContext<ISecondarySideBarContext>(SecondarySideBarContext);
    const { currentPage } = context;
    const params = context.params as { selectedTab?: string } | undefined;

    const secondaryItemsContainerRef = useRef<HTMLDivElement>(null);
    const [, setIsOverflown] = useState<{ top: boolean; bottom: boolean }>({
        top: false,
        bottom: false,
    });

    const [subSectionItems, setSubSectionItems] = useState<ReactNode[] | null>(null);

    const updateIsOverflown = () => {
        if (!secondaryItemsContainerRef || !secondaryItemsContainerRef.current) {
            return;
        }
        const { scrollHeight, scrollTop, clientHeight } = secondaryItemsContainerRef.current;
        setIsOverflown({
            top: scrollTop > 0,
            bottom: scrollHeight - scrollTop - clientHeight > 0,
        });
    };
    useEffect(() => {
        setTimeout(updateIsOverflown, 0);
    }, []);

    // Rebuilds the nav hierarchy whenever the route or items change. The build is async
    // (per-item href lookups via proNavigator), so when navObj changes mid-flight the
    // older call could resolve after the newer one and overwrite the state with stale
    // items — the cancelled flag, flipped on cleanup, prevents that overwrite.
    useEffect(() => {
        let cancelled = false;
        const loadItems = async () => {
            const updatedItems = await createSideNavHierarchy(navObj, currentPage, params?.selectedTab);
            if (!cancelled) {
                setSubSectionItems(updatedItems);
            }
        };
        void loadItems();
        return () => {
            cancelled = true;
        };
    }, [setSubSectionItems, currentPage, params, navObj]);

    return (
        <div
            ref={secondaryItemsContainerRef}
            onScroll={updateIsOverflown}
            style={{ height: "100%" }}
        >
            <div>{subSectionItems}</div>
        </div>
    );
};
